// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  PurchaseOrder,
  UpdatePurchaseOrderRequest,
  purchaseOrderSchema,
} from '@/purchase-order/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { z } from 'zod';

/**
 * Schema for validating the response from the update purchase order API endpoint.
 */
const updatePurchaseOrderResponseSchema = z.object({
  data: purchaseOrderSchema,
  metadata: responseMetadataSchema,
});

/**
 * Updates an existing purchase order with the specified details.
 *
 * @param purchaseOrderKey - The key of the purchase order to update
 * @param request - The purchase order details to update
 * @returns The updated purchase order
 */
export async function updatePurchaseOrder(
  purchaseOrderKey: string,
  request: UpdatePurchaseOrderRequest
): Promise<PurchaseOrder> {
  const url = `/v1/purchase-order/${purchaseOrderKey}`;
  const apiRequest = {
    ...request,
    promiseShipDate: serializeDateForAPI(request.promiseShipDate),
    promiseDeliveryDate: serializeDateForAPI(request.promiseDeliveryDate),
  };
  const response = await api.patch(url, apiRequest);

  const { data } = await parseResponse(
    updatePurchaseOrderResponseSchema,
    response.data,
    {
      schemaName: 'UpdatePurchaseOrder',
      endpoint: url,
    }
  );
  return data;
}
